import { ApiParameter, get, post, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";

const baseUrl = "/api/v1";
const baseWarehouseURL = `${baseUrl}/warehouse`;

export interface AddDefaultWHParameter extends ApiParameter {
    id: string;
}

export interface UpdatePartNumberParameter extends ApiParameter {
    erpNumber: string;
    customerPartNumber: string;
}

export interface ProductCategoriesParameter {
    productIds: string[];
    type: string;
}

export interface ProductCategoriesModel {
    id: string;
    erpNumber: string;
    categories: {
        item_category: string;
        item_category2: string;
        item_category3: string;
    };
    categoriesIds: {
        item_category: string;
        item_category2: string;
        item_category3: string;
    };
    brandName: string | null;
    customProperties: Record<string, unknown>;
    attributes: {
        [key: string]: string;
    };
}

export interface StatementRecord {
    label: string;
    balance: number;
}

export interface StatementTableModel {
    records: StatementRecord[];
}

export interface SystemListValue {
    id: string;
    name: string;
    description: string;
    additionalInfo: string;
    systemListId: string;
    deactivateOn: string | null;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    customProperties: any[];
}

export interface SystemList {
    id: string;
    name: string;
    description: string;
    additionalInfo: string;
    deactivateOn: string | null;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    customProperties: any[];
    values: SystemListValue[];
}

export interface TransactionType {
    id: string;
    erpCode: string;
    orderTypeCode: string;
    displayName: string;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    customProperties: any[];
}

export async function getAllWarehouses(): Promise<ServiceResult<WarehouseModel[]>> {
    const result = await get<WarehouseModel[]>(`${baseWarehouseURL}/getAllWarehouses/`);

    return {
        successful: true,
        result,
    };
}

export async function getDefaultWarehouse(): Promise<ServiceResult<WarehouseModel>> {
    const result = await get<WarehouseModel>(`${baseWarehouseURL}`);

    return {
        successful: true,
        result,
    };
}

export async function getWarehouseByName(name: string): Promise<ServiceResult<WarehouseModel>> {
    const result = await get<WarehouseModel>(`${baseWarehouseURL}/${name}`);

    return {
        successful: true,
        result,
    };
}

export async function setDefaultWarehouse(parameter: AddDefaultWHParameter): Promise<ServiceResult<boolean>> {
    try {
        const result = await post<boolean>(
            `${baseWarehouseURL}/setDefault?warehouseId=${parameter.id}`,
            parameter.id as any,
        );

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getProductCategories(
    params: ProductCategoriesParameter,
): Promise<ServiceResult<ProductCategoriesModel[]>> {
    try {
        const result = await post<{ productProperties: ProductCategoriesModel[] }>(
            `${baseUrl}/productproperties`,
            params as any,
        );

        return {
            result: result.productProperties,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getStatementTable() {
    const result = await get<StatementTableModel>(`${baseUrl}/statement`);

    return {
        successful: true,
        result,
    };
}

export async function setCustomerPartNumber(erpNumber: string, customerPartNumber: string): Promise<any> {
    try {
        const result = await post<any>(`${baseUrl}/customer/partnumber`, {
            erpNumber,
            customerPartNumber,
        });

        return {
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getSystemList(name: string): Promise<ServiceResult<SystemList>> {
    try {
        const result = await get<SystemList>(`${baseUrl}/systemlist/getbyname?systemListName=${name}`);

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getTransactionTypes(): Promise<ServiceResult<TransactionType[]>> {
    try {
        const result = await get<TransactionType[]>(`${baseUrl}/transactiontype`);

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getUserProperties(id: string): Promise<ServiceResult<TransactionType[]>> {
    try {
        const result = await get<TransactionType[]>(`${baseUrl}/userproperties?userId=${id}&_=${Date.now()}`);

        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function setDefaultTransactionType(transactionTypeId: string, isDefault: string): Promise<any> {
    try {
        const result = await post<any>(`${baseUrl}/user/defaulttransaction`, {
            transactionTypeId,
            isDefaultTransaction: isDefault,
        });

        return {
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function setDefaultService(serviceId: string, isDefault: string): Promise<any> {
    try {
        const result = await post<any>(`${baseUrl}/user/defaultService`, {
            serviceId,
            isDefaultService: isDefault,
        });

        return {
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export async function getOrderPrint(orderNumber: string, stEmail: string, stPostalCode: string): Promise<any> {
    try {
        const response = await fetch(`${baseUrl}/orderprint/printed`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/pdf",
            },
            body: JSON.stringify({ orderNumber, stEmail, stPostalCode }),
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.statusText}`);
        }

        const jsonResponse = await response.json();

        if (!jsonResponse.fileData) {
            throw new Error("No PDF data received from the server");
        }

        const blob = new Blob([Uint8Array.from(atob(jsonResponse.fileData), c => c.charCodeAt(0))], { type: "application/pdf" });
        window.open(URL.createObjectURL(blob), "_blank");
        
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching PDF:", error);
    }
}


export async function setConvertQuote(orderNumber: string, poNumber: string, specialInstructions: string): Promise<any> {
    try {
        const result = await post<any>(`${baseUrl}/quoteconverter`, {
            orderNumber,
            poNumber,
            specialInstructions,
        });

        return {
            successful: true,
            result
        };
    } catch (error) {
        if ("status" in error && error.status === 400 && error.errorJson && error.errorJson.message) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}